import { Component, OnInit, OnDestroy, Input, Output } from '@angular/core';

// ionic
import { ModalController } from '@ionic/angular';
import { Events } from "../../helpers/events";

// other libraries
import { TranslateService } from "@ngx-translate/core";

// rxjs
import { Subject } from "rxjs";

// services
import { PlatformService } from "../../services/platform.service";
import { ConversationService } from "../../services/conversation.service";
import { ChatGroupsService } from "../../services/chat-groups.service";
import { OverlayService } from "../../services/overlay.service";

// models
import { Conversation } from "../../models/conversation";
import { ConversationMessage } from "../../models/conversation-message";
import { ChatGroup } from "../../models/chat-group";
import { ChatGroupMessage } from "../../models/chat-group-message";

// config
import { imageExtList } from "../../settings/dropzone.settings";

@Component({
    selector: 'message-file-form',
    templateUrl: './message-file-form.component.html',
    styleUrls: ['./message-file-form.component.scss'],
})
export class MessageFileFormComponent implements OnInit, OnDestroy {

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * validate state
     *
     * @type {boolean}
     */
    public validate: boolean = false;

    /**
     * Validation errors
     *
     * @type {object}
     */
    public validationErrors: object = {};

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;
    /**
     * show loading
     *
     * @type {boolean}
     */
    public loading: boolean = false;

    public submitting: boolean = false;

    /**
     * parent object of message
     *
     * @type {ChatGroup | Conversation}
     */
    @Input() parentObject: ChatGroup | Conversation;

    /**
     * message we want to send
     *
     * @type {ConversationMessage | ChatGroupMessage}
     */
    @Input() message: ConversationMessage | ChatGroupMessage;

    /**
     * original name of file
     *
     * @type {string}
     */
    @Input() filename: string;

    public image: boolean = false;

    constructor(
        public plt: PlatformService,
        public translate: TranslateService,
        public appEvents: Events,
        public modalController: ModalController,
        public conversationService: ConversationService,
        public chatGroupsService: ChatGroupsService,
        public overlayService: OverlayService
    ) {

        // user language
        this.userLang = this.translate.currentLang;
    }

    ngOnInit() {
        this.image = this.isImage();
    }

    /**
     * determine if the file is image or not
     */
    private isImage() {
        if (!this.filename) {
            return false;
        }

        let ext = this.filename.split('.').pop();
        return imageExtList.indexOf(ext) !== -1;
    }

    public send() {
        // Prevent empty messages
        if (!this.message || this.submitting) {
            return;
        } else {
            this.submitting = true;
            let parentId = this.parentObject.id;

            if (this.message instanceof ChatGroupMessage) {
                let subscription = this.chatGroupsService.sendMessage(parentId, <ChatGroupMessage>this.message, this.filename)
                    .subscribe(response => {
                        this.handleSuccess(response);
                    }, error => {
                        this.handleError(error);
                    });
                this.plt.activeSubscriptions.push(subscription);
            } else {
                let subscriptionConversation = this.conversationService.sendMessage(parentId, <ConversationMessage>this.message, this.filename)
                    .subscribe(response => {
                        this.handleSuccess(response);
                    }, error => {
                        this.handleError(error);
                    });
                this.plt.activeSubscriptions.push(subscriptionConversation);
            }
        }
    }

    private handleSuccess(response) {
        // update last message in box with conversation detail
        this.submitting = false;

        this.closeSaved(response);
    }

    private handleError(response) {
        const data = response.error;

        // no response -> propably too big image
        if (!data || (this.image && !data.message)) {
            this.overlayService.showError(this.translate.instant('VALIDATION_IMAGE_TOO_LARGE', {
                maxSize: 4
            }));
        }
        else {
            if (data.fields) {
                for (const field in data.fields) {
                    if (data.fields.hasOwnProperty(field)) {
                        this.validationErrors[field] = data.fields[field].join(' ');
                    }
                }
            }

            // translate message for corrupted image
            if (this.image && data.message.indexOf('Unable to decode') != -1) {
                this.overlayService.showError(this.translate.instant('UPLOAD_IMAGE_ERROR'));
            } else {
                this.overlayService.showError(data.message);
            }
        }


        this.submitting = false;

        if (data && data.leave_group) {
            this.appEvents.publish('messages:refresh:list');
            this.backClicked();
        }
    }

    /**
     * close modal window
     *
     */
    public backClicked() {
        this.modalController.dismiss({ action: 'close' });
    }

    /**
     * close modal window
     *
     */
    public closeSaved(response?) {
        this.modalController.dismiss({
            action: 'close',
            response: response
        });
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
